import { v4 as uuidv4 } from "uuid";
import { INTERPRETER_BASE_URL } from "../config/runtime";

const A2A_URL = INTERPRETER_BASE_URL
  ? `${INTERPRETER_BASE_URL}/api/a2a`
  : `/api/a2a`;

export const VALID_FILE_EXTENSIONS = [
  ".step",
  ".pdf",
  ".jpg",
  ".jpeg",
  ".png",
  ".svg",
  ".dpm",
];

// Returns { existingFileNames, orgDuplicateFileNames, orgDuplicateProposedNames }
// Throws on network/HTTP error — callers should fall back to uploading without the check.
export const checkDuplicateFiles = async (userId, sessionId, fileNames) => {
  const res = await fetch(A2A_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: uuidv4(),
      method: "CheckDuplicateFiles",
      params: {
        metadata: {
          user_id: userId.toString(),
          ...(localStorage.getItem("selectedOrganizationId")
            ? {
                organization_id: localStorage.getItem("selectedOrganizationId"),
              }
            : {}),
        },
        message: {
          parts: [
            {
              type: "data",
              data: { session_id: sessionId, file_names: fileNames },
            },
          ],
        },
      },
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  const data = json?.result?.artifact?.parts?.[0]?.data ?? {};
  return {
    existingFileNames: data.existing_file_names ?? [],
    orgDuplicateFileNames: data.org_duplicate_file_names ?? [],
    orgDuplicateProposedNames: data.org_duplicate_proposed_names ?? {},
  };
};

const orgIdMeta = () =>
  localStorage.getItem("selectedOrganizationId")
    ? { organization_id: localStorage.getItem("selectedOrganizationId") }
    : {};

const getUploadSignedUrls = async (userId, sessionId, fileEntries) => {
  const res = await fetch(A2A_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: uuidv4(),
      method: "GetUploadSignedUrl",
      params: {
        metadata: { user_id: userId.toString(), ...orgIdMeta() },
        message: {
          parts: [
            {
              type: "data",
              data: {
                session_id: sessionId,
                files: fileEntries.map(e => ({
                  file_name: e.name,
                  request_id: e.request_id,
                  replace_file: e.replace_file ?? false,
                  ...(e.folder_id != null ? { folder_id: e.folder_id } : {}),
                })),
              },
            },
          ],
        },
      },
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  const results = json?.result?.artifact?.parts?.[0]?.data?.results;
  if (!results) throw new Error("No signed URLs returned");
  return results;
};

const confirmUpload = async (userId, sessionId, signedUrlData, fileEntry) => {
  const res = await fetch(A2A_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: uuidv4(),
      method: "ConfirmUpload",
      params: {
        metadata: { user_id: userId.toString(), ...orgIdMeta() },
        message: {
          parts: [
            {
              type: "data",
              data: {
                session_id: sessionId,
                gcs_path: signedUrlData.gcs_path,
                bucket_name: signedUrlData.bucket_name,
                file_name: fileEntry.name,
                request_id: fileEntry.request_id,
                folder_id: signedUrlData.folder_id ?? null,
                replace_file: fileEntry.replace_file ?? false,
              },
            },
          ],
        },
      },
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  const data = json?.result?.artifact?.parts?.[0]?.data;
  if (!data?.success) throw new Error("ConfirmUpload failed");
  return data;
};

const putToGcsAndConfirm = async (userId, sessionId, fileEntry, urlResult) => {
  console.log(`[upload] "${fileEntry.name}" uploading directly to GCS`);
  const gcsStart = Date.now();
  const gcsRes = await fetch(urlResult.signed_url, {
    method: "PUT",
    body: fileEntry.file,
    headers: { "Content-Type": "application/octet-stream" },
  });
  console.log(
    `[upload] "${fileEntry.name}" GCS response ${gcsRes.status} in ${((Date.now() - gcsStart) / 1000).toFixed(1)}s`,
  );
  if (!gcsRes.ok) {
    throw new Error(`GCS upload failed: HTTP ${gcsRes.status}`);
  }

  console.log(`[upload] "${fileEntry.name}" confirming upload`);
  const confirmData = await confirmUpload(
    userId,
    sessionId,
    urlResult,
    fileEntry,
  );
  return {
    file_name: confirmData.file_name,
    file_id: confirmData.file_id,
    request_id: fileEntry.request_id,
    status: "SAVED_TO_BLOB_STORE",
    organization_id: confirmData.organization_id ?? urlResult.organization_id,
  };
};

// fileEntries: [{ name, file, request_id, replace_file?, folder_id? }]
// Returns { uploadedFiles, failedEntries }.
export const sendUploadFiles = async (
  userId,
  sessionId,
  fileEntries,
  { onFileUploaded } = {},
) => {
  console.log(
    `[upload] requesting signed URLs for ${fileEntries.length} file(s): ${fileEntries.map(f => f.name).join(", ")}`,
  );
  const uploadStart = Date.now();

  let signedUrlResults;
  try {
    signedUrlResults = await getUploadSignedUrls(
      userId,
      sessionId,
      fileEntries,
    );
    console.log(
      `[upload] got ${signedUrlResults.length} signed URL(s) in ${Date.now() - uploadStart}ms`,
    );
  } catch (e) {
    console.error(`[upload] failed to get signed URLs: ${e.message}`);
    return {
      uploadedFiles: [],
      failedEntries: fileEntries.map(entry => ({
        entry,
        error: e.message ?? "Failed to get upload URL",
      })),
    };
  }

  const entryByRequestId = Object.fromEntries(
    fileEntries.map(e => [e.request_id, e]),
  );

  const results = await Promise.allSettled(
    signedUrlResults.map(async urlResult => {
      const entry = entryByRequestId[urlResult.request_id];
      const file = await putToGcsAndConfirm(
        userId,
        sessionId,
        entry,
        urlResult,
      );
      try {
        onFileUploaded?.(file);
      } catch {}
      return file;
    }),
  );

  const uploadedFiles = [];
  const failedEntries = [];
  results.forEach((result, i) => {
    if (result.status === "fulfilled") {
      uploadedFiles.push(result.value);
    } else {
      const urlResult = signedUrlResults[i];
      const entry = entryByRequestId[urlResult?.request_id] ?? fileEntries[i];
      failedEntries.push({
        entry,
        error: result.reason?.message ?? "Upload failed",
      });
    }
  });

  console.log(
    `[upload] done — ${uploadedFiles.length} succeeded, ${failedEntries.length} failed | total: ${((Date.now() - uploadStart) / 1000).toFixed(1)}s`,
  );
  return { uploadedFiles, failedEntries };
};

async function fetchFiles(userId, sessionId, fileType) {
  const res = await fetch(A2A_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: uuidv4(),
      method: "ListFiles",
      params: {
        metadata: {
          user_id: userId.toString(),
          ...(localStorage.getItem("selectedOrganizationId")
            ? {
                organization_id: localStorage.getItem("selectedOrganizationId"),
              }
            : {}),
        },
        message: {
          parts: [
            {
              type: "data",
              data: {
                session_id: sessionId,
                search_all_folders: true,
                filter_options: {
                  search_str: null,
                  sort_by: null,
                  sort_order: null,
                  show_only_starred: null,
                  file_type: fileType,
                },
                pagination_options: { page_num: 1, page_size: 200 },
                folder_id: null,
                source: "folder_open",
              },
            },
          ],
        },
      },
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = await res.json();
  return json.result?.artifact?.parts?.flatMap(p => p.data?.files || []) ?? [];
}

export const listPdfFiles = async (userId, sessionId) => {
  const files = await fetchFiles(userId, sessionId, "PDF");
  return files.filter(f => f.status === "INGESTION_COMPLETED");
};

export const listUserImageFiles = async (userId, sessionId) => {
  const files = await fetchFiles(userId, sessionId, "IMAGE");
  return files.filter(f => f.status === "INGESTION_COMPLETED");
};

export const cancelFileUpload = async (userId, sessionId, fileId) => {
  const res = await fetch(A2A_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: uuidv4(),
      method: "CancelFileUpload",
      params: {
        metadata: { user_id: userId.toString() },
        message: {
          parts: [
            { type: "data", data: { session_id: sessionId, file_id: fileId } },
          ],
        },
      },
    }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};
